import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DailyAverageChart.scss';
import * as am5 from '@amcharts/amcharts5';
import { useAppSelector } from '../../hooks/storeHooks';
import { createTestChart, getSortedChartDataForTest } from '../../utilities/TestChartUtil';
import Loader from '../../features/common/Loader/Loader';
import EmptyState from '../empty-state/EmptyState';
import FullScreenIcon from '../../features/common/FullScreenIcon/FullScreenIcon';
import { exportChart } from '../../utilities/ExportChart';
import DownloadChart from '../time-series-chart/download/downloadChart';
import { ProtectAction } from '../../rbac';

interface DailyAverageChartProps {
  dailyAverageChartData: any;
  rPermission: { [key: string]: boolean };
}

const DailyAverageChart: React.FC<DailyAverageChartProps> = ({ dailyAverageChartData, rPermission }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chartData = useAppSelector((state: any) => state.AssetTrendDataSlice.data);
  const loading = useAppSelector((state: any) => state.AssetTrendDataSlice.loading);
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const startDate = useAppSelector((state: any) => state.AssetTrendDataSlice.startDate);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);

  const parameterData = dailyAverageChartData?.widgetProperty.widgetProperties.parameterConfig[0]?.parameters;

  const sortedData = useMemo(() => getSortedChartDataForTest(chartData, parameterData), [chartData, parameterData]);

  const hasPlottableData = sortedData.some((d: any) => Array.isArray(d.dataPoints) && d.dataPoints.length > 0);

  // Fullscreen handlers
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  // Export handlers
  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById('daily-average-chart-content');
    if (!element) return;
    await exportChart(element, getExportType(option), 'DailyAverageChart', undefined, undefined, 'Daily Averages');
  };

  // Chart creation effect
  useEffect(() => {
    if (!chartRef.current || !hasPlottableData) {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
      return;
    }

    // Dispose previous chart
    if (rootRef.current) {
      rootRef.current.dispose();
      rootRef.current = null;
    }

    // Create new chart
    const root = createTestChart(chartRef.current, {
      chartLabel: 'Daily averages',
      chartData,
      parameterData,
      chartViewOptions,
      sortedData,
      startDate,
      endDate,
    });

    rootRef.current = root;

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
    };
  }, [chartData, chartViewOptions, sortedData, parameterData, hasPlottableData]);

  if (loading) return <Loader />;

  return (
    <section
      ref={containerRef}
      className={`ogl-chart-container ${isFullscreen ? 'fullscreen' : ''}`}
      style={!isFullscreen ? { background: 'transparent' } : undefined}
    >
      <header className='ogl-chart-header'>
        <div className='chart-title'>
          <h2>Daily averages</h2>
        </div>
        <div className='chart-actions'>
          <ProtectAction canAccess={rPermission.canDailyAverageExpand}>
            <button className='chart-action-button' onClick={toggleFullscreen}>
              <FullScreenIcon />
            </button>
          </ProtectAction>
          <button className='chart-action-button'>
            <DownloadChart
              onClick={handleChartDownload}
              canExportPdf={rPermission.canDailyAverageExportPdf}
              canExportJpg={rPermission.canDailyAverageExportJpg}
            />
          </button>
        </div>
      </header>

      {isFullscreen && <div className='chart-divider'></div>}

      {hasPlottableData ? (
        <div id='daily-average-chart-content'>
          <div ref={chartRef} className='daily-average-chart-container'></div>
        </div>
      ) : (
        <EmptyState message='No data available for the selected date range. Please adjust your selection and try again' />
      )}
    </section>
  );
};

export default DailyAverageChart;
